import { PageContentModel, PlaceholderContentModel, RootContentModel } from './api/ContentDeliveryApi.schemas'
import { sortBy } from './SortBy'

type ContentModel = RootContentModel | PageContentModel | PlaceholderContentModel

const expand = (depth => 'properties[$all['.repeat(depth - 1) + 'properties[$all' + ']]'.repeat(depth - 1) + ']')(10)

export type UmbracoPage = ContentModel & {
	parentId: string
	culture: string
	cultures: Record<string, { path: string, queryString?: string, startItem: { id: string, path: string } }>
	children: UmbracoPage[]
}

export function getAlternateLinks(page: UmbracoPage) {
	const alternateLinks: Record<string, string> = {}
	const slugIndex = new URL(page.route.path).pathname.split("/").indexOf("*")
	const slug = location.pathname.split("/").at(slugIndex) || ""
	sortBy(Object.keys(page?.cultures || {}), x => x == "en" ? 0 + x : ["ko", "ja", "zh"].includes(x) ? 2 + x : 1 + x).forEach(lang => {
		const url = new URL(page.cultures[lang].path + window.location.search)
		url.pathname = url.pathname.split("/").map(x => x === "*" ? slug : x).join("/")
		alternateLinks[lang] = url.toString()
	})

	return alternateLinks
}

export async function getPages(baseUrl: string, rootId: string) {
	// const root = await (await fetch(`${baseUrl}/umbraco/delivery/api/v2/content/item/${rootId}`)).json() as RootContentResponseModel
	// const cultures = Object.keys(root.cultures)

	const cultures = ['en', 'de', 'es', 'fr', 'it', 'ko', 'ja', 'zh']

	const pages = (await Promise.all(cultures.map(async culture => {
		const localisedPages = (await (await fetch(`${baseUrl}/umbraco/delivery/api/v2/content?sort=sortOrder:asc&take=100000&expand=${expand}`, { 'headers': { 'Accept-Language': culture } })).json()).items as UmbracoPage[]
		localisedPages.forEach(x => x.culture = culture)
		return localisedPages
	}))).flat()

	pages.forEach(page => page.parentId = pages.find(x => x.route.path == (page.route.path.substring(0, page.route.path.lastIndexOf('/')) || '/') && x.culture == page.culture && x.route.startItem.id == page.route.startItem.id && x.id != page.id)?.id)
	pages.forEach(page => page.children = pages.filter(x => x.parentId == page.id && x.culture == page.culture))

	return pages // .filter(x => !x.parentId)
}

export async function getCachedPages(baseUrl: string, rootId: string) {
	// return await getPages(baseUrl, rootId)
	const promise = getPages(baseUrl, rootId)
	promise.then(pages => localStorage.setItem('pages', JSON.stringify(pages)))

	const stored = localStorage.getItem('pages')

	if (stored) {
		return JSON.parse(stored) as UmbracoPage[]
	} else {
		return await promise
	}
}

export async function getContent() {
	const pages = await getCachedPages(import.meta.env.VITE_CONTENT_BASE, '')
	const segments = decodeURI(document.location.pathname).split('/').filter(Boolean)

	const matches = pages.filter(x => {
		const pageSegments = x.route.path.split('/').filter(Boolean)
		return pageSegments.length == segments.length && pageSegments.every((x, i) => x == segments[i] || x.startsWith('*'))
	})

	const matchesSorted = sortBy(matches, x => !x.route.path.includes('*') ? 0 : 100 - x.route.path.split('/').findIndex(x => x.startsWith('*')))
	let page = matchesSorted.at(0)

	const breadcrumb = [page]
	while (page) {
		page = pages.find(x => x.id === page.parentId && x.culture == page.culture)
		if (!page) break
		breadcrumb.unshift(page)
	}

	return {
		breadcrumb,
		pages
	}
}

export async function getLocalContent() {
	const pages = await (await fetch('/content/pages.json')).json() as {
		name: string
		path: string
		culture: string
		id: string
		parentId: string
		rootId: string
		showInNavigation: string
		icon: string
		updateDate: string
	}[]

	const segments = decodeURI(document.location.pathname).split('/').filter(Boolean)

	const matches = pages.filter(x => {
		const pageSegments = x.path.split('/').filter(Boolean)
		return pageSegments.length == segments.length && pageSegments.every((x, i) => x == segments[i] || x.startsWith('*'))
	})
	const matchesSorted = sortBy(matches, x => !x.path.includes('*') ? 0 : 100 - x.path.split('/').findIndex(x => x.startsWith('*')))
	let page = matchesSorted.at(0)

	const breadcrumb = [page]
	while (page) {
		page = pages.find(x => x.id === page.parentId && x.culture == page.culture)
		if (!page) break
		breadcrumb.unshift(page)
	}

	const breadcrumbPages = await Promise.all(breadcrumb.map(async x => (await (await fetch(`/content/${x.culture}/${x.id}.json`)).json()) as UmbracoPage))

	return {
		breadcrumb: breadcrumbPages,
		pages
	}
}
